import expenseQueryMatch from '@/function/expense/expense.query.match';
import expenseQueryOrder from '@/function/expense/expense.query.order';
import expenseTotalsDelta from '@/function/expense/expense.totals.delta';
import { expenseApi } from '@/store/api/expense/expense.api';
import { dropExpenseCalendar, writeExpenseCalendar } from '@/store/api/expense/expense.calendar.cache';
import { store, type TypeAppDispatch } from '@/store/store';
import type { TypeExpense } from '@/types/expense/types.expense';
import type { TypeExpensePage, TypeExpenseQuery } from '@/types/expense/types.expense.query';

const REFRESH_MS = 2_000;

let refreshTimer: ReturnType<typeof setTimeout> | null = null;

const cachedLists = (): TypeExpenseQuery[] =>
    expenseApi.util.selectCachedArgsForQuery(store.getState(), 'listExpensesPage') as TypeExpenseQuery[];

const cachedTotals = (): TypeExpenseQuery[] =>
    expenseApi.util.selectCachedArgsForQuery(store.getState(), 'readExpenseTotals') as TypeExpenseQuery[];

const SEEN_MAX = 500;
const seen = new Map<string, TypeExpense | null>();

const remember = (id: string, expense: TypeExpense | null): void => {
    seen.delete(id);
    seen.set(id, expense);
    if (seen.size > SEEN_MAX) seen.delete(seen.keys().next().value as string);
};

const previousOf = (id: string): TypeExpense | null | undefined => {
    if (seen.has(id)) return seen.get(id);
    const state = store.getState();
    const direct = expenseApi.endpoints.readExpense.select(id)(state).data;
    if (direct) return direct;
    for (const query of cachedLists()) {
        for (const page of expenseApi.endpoints.listExpensesPage.select(query)(state).data?.pages ?? []) {
            const found = page.items.find((item) => item.id === id);
            if (found) return found;
        }
    }
    return undefined;
};

const TOTAL_KEYS = ['count', 'totalCents', 'openCents', 'paid', 'due', 'overdue'] as const;

const patchTotals = (dispatch: TypeAppDispatch, before: TypeExpense | null | undefined, after: TypeExpense | null): void => {
    for (const query of cachedTotals()) {
        const delta = expenseTotalsDelta(before, after, query);
        if (!delta) {
            void dispatch(expenseApi.endpoints.readExpenseTotals.initiate(query, { subscribe: false, forceRefetch: true }));
            continue;
        }
        if (TOTAL_KEYS.every((key) => delta[key] === 0)) continue;
        dispatch(expenseApi.util.updateQueryData('readExpenseTotals', query, (draft) => {
            for (const key of TOTAL_KEYS) draft[key] += delta[key];
        }));
    }
};

const cachedReviews = (): string[] =>
    expenseApi.util.selectCachedArgsForQuery(store.getState(), 'readExpenseReview') as string[];

const scheduleRefresh = (dispatch: TypeAppDispatch): void => {
    if (refreshTimer) return;
    refreshTimer = setTimeout(() => {
        refreshTimer = null;
        dispatch(expenseApi.util.invalidateTags(['ExpenseSummary', 'ExpenseReview']));
    }, REFRESH_MS);
};

const removeFrom = (pages: TypeExpensePage[], id: string): boolean => {
    for (const page of pages) {
        const at = page.items.findIndex((item) => item.id === id);
        if (at >= 0) { page.items.splice(at, 1); return true; }
    }
    return false;
};

const insertInto = (pages: TypeExpensePage[], expense: TypeExpense, query: TypeExpenseQuery): void => {
    const order = expenseQueryOrder(query.sort, query.reverse);
    for (const page of pages) {
        const at = page.items.findIndex((item) => order(expense, item) < 0);
        if (at >= 0) { page.items.splice(at, 0, expense); return; }
    }
    const last = pages[pages.length - 1];
    if (last && !last.page?.hasMore) last.items.push(expense);
};

const place = (pages: TypeExpensePage[], expense: TypeExpense, query: TypeExpenseQuery): void => {
    const verdict = expenseQueryMatch(expense, query);
    if (verdict === null) {
        for (const page of pages) {
            const at = page.items.findIndex((item) => item.id === expense.id);
            if (at >= 0) { page.items[at] = expense; return; }
        }
        return;
    }
    removeFrom(pages, expense.id);
    if (verdict) insertInto(pages, expense, query);
};

export const writeExpense = (dispatch: TypeAppDispatch, expense: TypeExpense, known?: { before: TypeExpense | null }): void => {
    const before = previousOf(expense.id);
    patchTotals(dispatch, before === undefined && known ? known.before : before, expense);
    remember(expense.id, expense);
    dispatch(expenseApi.util.updateQueryData('readExpense', expense.id, () => expense));
    for (const query of cachedLists()) {
        dispatch(expenseApi.util.updateQueryData('listExpensesPage', query, (draft) => { place(draft.pages, expense, query); }));
    }
    for (const today of cachedReviews()) {
        dispatch(expenseApi.util.updateQueryData('readExpenseReview', today, (draft) => {
            const at = draft.items.findIndex((item) => item.id === expense.id);
            if (at >= 0) draft.items[at] = expense;
        }));
    }
    writeExpenseCalendar(dispatch, expense);
    scheduleRefresh(dispatch);
};

export const dropExpense = (dispatch: TypeAppDispatch, id: string): void => {
    patchTotals(dispatch, previousOf(id), null);
    remember(id, null);
    for (const query of cachedLists()) {
        dispatch(expenseApi.util.updateQueryData('listExpensesPage', query, (draft) => { removeFrom(draft.pages, id); }));
    }
    for (const today of cachedReviews()) {
        dispatch(expenseApi.util.updateQueryData('readExpenseReview', today, (draft) => {
            draft.items = draft.items.filter((item) => item.id !== id);
        }));
    }
    dropExpenseCalendar(dispatch, id);
    scheduleRefresh(dispatch);
};

export const patchExpense = (dispatch: TypeAppDispatch, id: string, fields: Partial<TypeExpense>): (() => void) => {
    const original = previousOf(id);
    if (original !== undefined) remember(id, original);
    const patches = [
        dispatch(expenseApi.util.updateQueryData('readExpense', id, (draft) => { Object.assign(draft, fields); })),
        ...cachedLists().map((query) => dispatch(expenseApi.util.updateQueryData('listExpensesPage', query, (draft) => {
            for (const page of draft.pages) {
                const entry = page.items.find((item) => item.id === id);
                if (entry) Object.assign(entry, fields);
            }
        }))),
    ];
    return () => { for (const patch of patches) patch.undo(); };
};
